// ── Account access audit ────────────────────────────────────────────────────────
// Cross-user "who viewed this record" trail. Whenever an authorized viewer (the
// owner, or a guardian/service-provider signed in with their own Google account)
// opens a profile, we append one entry to users/{ownerUid}/profiles/{pid}/access.
// Only the OWNER can read the history (firestore.rules); entries are append-only.
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { fbDb } from './firebase';

export interface AccessEntry {
  id: string;
  viewerUid: string;
  viewerEmail: string;
  viewerName: string;
  role: 'owner' | 'guardian';   // owner = you; guardian = someone you granted access
  action: 'view';
  at: number;                   // epoch ms (from the server Timestamp)
}

const accessCol = (ownerUid: string, pid: string) => collection(fbDb, 'users', ownerUid, 'profiles', pid, 'access');

// One write per (viewer, profile) per app session — navigating around a profile
// shouldn't spam the log; a fresh page load / new sign-in records a new view.
const logged = new Set<string>();

export interface Viewer { uid: string; email: string; name: string; isOwner: boolean }

/** Record that `viewer` opened profile `pid` (owned by `ownerUid`). Fire-and-forget. */
export async function logAccess(ownerUid: string, pid: string, viewer: Viewer): Promise<void> {
  if (!ownerUid || !pid || !viewer.uid) return;
  const key = `${viewer.uid}:${ownerUid}/${pid}`;
  if (logged.has(key)) return;
  logged.add(key);
  try {
    await addDoc(accessCol(ownerUid, pid), {
      viewerUid: viewer.uid,
      viewerEmail: (viewer.email || '').toLowerCase(),
      viewerName: viewer.name || '',
      role: viewer.isOwner ? 'owner' : 'guardian',
      action: 'view',
      at: serverTimestamp(),
    });
  } catch (_) { logged.delete(key); /* allow a later retry */ }
}

/** The access history for a profile over the last `days` days (owner only), newest first. */
export async function listAccess(ownerUid: string, pid: string, days = 30): Promise<AccessEntry[]> {
  const cutoff = Date.now() - days * 86400_000;
  const snap = await getDocs(query(accessCol(ownerUid, pid), orderBy('at', 'desc')));
  const out: AccessEntry[] = [];
  for (const d of snap.docs) {
    const v = d.data() as any;
    const at = v.at?.toMillis ? v.at.toMillis() : (v.at?.seconds ? v.at.seconds * 1000 : 0);
    if (at && at < cutoff) continue;
    out.push({
      id: d.id,
      viewerUid: v.viewerUid || '',
      viewerEmail: v.viewerEmail || '',
      viewerName: v.viewerName || '',
      role: v.role === 'owner' ? 'owner' : 'guardian',
      action: 'view',
      at,
    });
  }
  return out;
}
